import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Users, Send, Lightbulb, Church, Megaphone, BarChart3, Calendar, HandHeart,
  DollarSign, Cake, PartyPopper, MessageSquare, Zap, CheckCircle2, Star, User,
  Loader2, AlertCircle,
  // ---- icons for Recent Activity ----
  Plus, Pencil, Trash2, LogIn, LogOut, UserPlus, UserCog, UserMinus,
  UserCheck, UserX, PlayCircle, PauseCircle, Ban, Unlock, KeyRound,
  CalendarClock, Rocket, Download, Upload, Settings, Building2, Lock,
  Activity as ActivityIcon,
  // ---- pagination icons ----
  ChevronLeft, ChevronRight,
} from 'lucide-react';

import {
  getDashboard,
  clearError,
  clearApiMessage,
  selectDashboardStats,
  selectSmsActivity,
  selectBirthdays,
  selectInboxAlerts,
  selectAutomations,
  selectPlatformHealth,
  selectLatestPoll,
  selectRecentActivity,
  selectGroupsSnapshot,
  selectDashboardLoading,
  selectDashboardError,
  selectDashboardApiMessage,
} from '../../store/slices/main/dashboardSlice';
import { shouldShowApiToast } from '../../utils/helpers';

/* =============================================================
 *  ACTIVITY ICON MAP
 *  Maps backend `type` → Lucide icon component.
 *  Falls back to <ActivityIcon /> for unknown types.
 * ============================================================= */
const ACTIVITY_ICONS = {
  created:                  Plus,
  updated:                  Pencil,
  deleted:                  Trash2,

  login:                    LogIn,
  logout:                   LogOut,

  user_created:             UserPlus,
  user_updated:             UserCog,
  user_deleted:             UserMinus,
  user_approved:            UserCheck,
  user_rejected:            UserX,
  user_activated:           PlayCircle,
  user_deactivated:         PauseCircle,
  user_blocked:             Ban,
  user_unblocked:           Unlock,

  password_changed:         KeyRound,
  profile_updated:          UserCog,

  broadcast_sent:           Send,
  broadcast_scheduled:      CalendarClock,
  campaign_created:         Rocket,

  poll_created:             BarChart3,
  member_imported:          Download,
  member_exported:          Upload,

  settings_updated:         Settings,
  organization_switched:    Building2,
  two_factor_toggled:       Lock,
};

const getActivityIcon = (type) => ACTIVITY_ICONS[type] || ActivityIcon;

/* =============================================================
 *  DASHBOARD
 * ============================================================= */
const Dashboard = ({ showToast }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const lastToastRef = useRef({ message: null, timestamp: 0 });

  // -------- selectors --------
  const stats           = useSelector(selectDashboardStats);
  const smsActivity     = useSelector(selectSmsActivity);
  const birthdays       = useSelector(selectBirthdays);
  const inboxAlerts     = useSelector(selectInboxAlerts);
  const automations     = useSelector(selectAutomations);
  const platformHealth  = useSelector(selectPlatformHealth);
  const latestPoll      = useSelector(selectLatestPoll);
  const recentActivity  = useSelector(selectRecentActivity);
  const groupsSnapshot  = useSelector(selectGroupsSnapshot);

  const loading   = useSelector(selectDashboardLoading);
  const error     = useSelector(selectDashboardError);
  const apiMessage = useSelector(selectDashboardApiMessage);

  // -------- local UI state: activity pagination --------
  const ACTIVITY_PER_PAGE = 5;
  const [activityPage, setActivityPage] = useState(1);

  // -------- initial fetch (request 50 activity items so paging is instant) --------
  useEffect(() => {
    dispatch(getDashboard({ activity_limit: 50 }));
  }, [dispatch]);

  // -------- API toast deduplication --------
  useEffect(() => {
    if (!apiMessage || !showToast || !shouldShowApiToast(apiMessage, !!error)) return;

    const messageKey = `${apiMessage}_${error ? 'error' : 'success'}`;
    const now = Date.now();

    if (
      lastToastRef.current.message === messageKey &&
      now - lastToastRef.current.timestamp < 2000
    ) return;

    lastToastRef.current = { message: messageKey, timestamp: now };
    showToast(apiMessage, error ? 'error' : 'success');

    setTimeout(() => {
      dispatch(clearApiMessage());
      dispatch(clearError());
    }, 500);
  }, [apiMessage, error, dispatch, showToast]);

  const handleNavigate = (path) => navigate(`/${path}`);

  /* =============================================================
   *  ACTIVITY PAGINATION (client-side)
   * ============================================================= */
  const activityTotal      = recentActivity.length;
  const activityTotalPages = Math.max(1, Math.ceil(activityTotal / ACTIVITY_PER_PAGE));

  // Clamp page if data shrinks (e.g. after a refresh)
  useEffect(() => {
    if (activityPage > activityTotalPages) {
      setActivityPage(activityTotalPages);
    }
  }, [activityPage, activityTotalPages]);

  const paginatedActivity = useMemo(() => {
    const start = (activityPage - 1) * ACTIVITY_PER_PAGE;
    return recentActivity.slice(start, start + ACTIVITY_PER_PAGE);
  }, [recentActivity, activityPage]);

  const goToActivityPage = (page) => {
    if (page >= 1 && page <= activityTotalPages) {
      setActivityPage(page);
    }
  };

  /* =============================================================
   *  HELPERS
   * ============================================================= */
  const formatNumber = (n) => {
    if (n === null || n === undefined) return '0';
    return Number(n).toLocaleString('en-US');
  };

  const maxBarValue = () => {
    const values = (smsActivity?.bars || []).map(b => b.value || 0);
    return values.length ? Math.max(...values, 1) : 1;
  };

  /* =============================================================
   *  LOADING STATE
   * ============================================================= */
  if (loading && !stats) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 0' }}>
        <Loader2 size={48} style={{ animation: 'spin 1s linear infinite', color: 'var(--text-light)' }} />
        <div style={{ color: 'var(--text-light)', marginTop: '12px' }}>Loading dashboard…</div>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  /* =============================================================
   *  ERROR STATE (no cached data)
   * ============================================================= */
  if (error && !stats) {
    return (
      <div className="card" style={{ padding: '32px', textAlign: 'center' }}>
        <AlertCircle size={48} style={{ color: 'var(--red)', marginBottom: '12px' }} />
        <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--navy)', marginBottom: '6px' }}>
          Failed to load dashboard
        </div>
        <div style={{ fontSize: '13px', color: 'var(--text-mid)', marginBottom: '16px' }}>
          {error}
        </div>
        <button className="btn btn-gold btn-sm" onClick={() => dispatch(getDashboard({ activity_limit: 50 }))}>
          Retry
        </button>
      </div>
    );
  }

  /* =============================================================
   *  NORMALIZE DATA
   * ============================================================= */
  const s = stats || {};
  const totalMembers       = s.total_members || {};
  const smsSent            = s.sms_sent || {};
  const engagement         = s.engagement || {};
  const attendance         = s.attendance || {};

  const bars = smsActivity?.bars || [];
  const totals = smsActivity?.totals || {};
  const barMax = maxBarValue();

  const healthItems = platformHealth?.items || [];

  /* =============================================================
   *  RENDER
   * ============================================================= */
  return (
    <>
      {/* ================= HERO STATS ================= */}
      <div className="stats-grid">
        <div className="stat-card gold" style={{ cursor: 'pointer' }} onClick={() => handleNavigate('members')}>
          <div className="stat-label">Total Members</div>
          <div className="stat-value">{formatNumber(totalMembers.value)}</div>
          <div className="stat-change up">
            {totalMembers.change_label || `↑ ${totalMembers.change || 0} this month`}
          </div>
          <div style={{ marginTop: '10px' }}>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${totalMembers.active_percent || 0}%`, background: 'var(--gold)' }}></div>
            </div>
            <div style={{ fontSize: '10px', color: 'var(--text-light)', marginTop: '3px' }}>
              {totalMembers.active_percent || 0}% active
            </div>
          </div>
          <div className="stat-icon"><Users size={20} /></div>
        </div>

        <div className="stat-card green" style={{ cursor: 'pointer' }} onClick={() => handleNavigate('msglog')}>
          <div className="stat-label">SMS Sent This Month</div>
          <div className="stat-value">{formatNumber(smsSent.value)}</div>
          <div className="stat-change up">
            {smsSent.change_percent >= 0 ? '↑' : '↓'} {Math.abs(smsSent.change_percent || 0)}% vs last month
          </div>
          <div style={{ marginTop: '10px' }}>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${smsSent.delivery_rate || 0}%`, background: 'var(--green)' }}></div>
            </div>
            <div style={{ fontSize: '10px', color: 'var(--text-light)', marginTop: '3px' }}>
              {smsSent.delivery_rate || 0}% delivered
            </div>
          </div>
          <div className="stat-icon"><Send size={20} /></div>
        </div>

        <div className="stat-card blue" style={{ cursor: 'pointer' }} onClick={() => handleNavigate('health')}>
          <div className="stat-label">Avg Engagement Score</div>
          <div className="stat-value">{engagement.value || 0}%</div>
          <div className="stat-change up">↑ 4pts this quarter</div>
          <div style={{ marginTop: '10px' }}>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${engagement.value || 0}%`, background: 'var(--blue)' }}></div>
            </div>
            <div style={{ fontSize: '10px', color: 'var(--text-light)', marginTop: '3px' }}>
              {engagement.highly_engaged_percent || 0}% highly engaged
            </div>
          </div>
          <div className="stat-icon"><Lightbulb size={20} /></div>
        </div>

        <div className="stat-card purple" style={{ cursor: 'pointer' }} onClick={() => handleNavigate('attendance')}>
          <div className="stat-label">Sunday Attendance</div>
          <div className="stat-value">{formatNumber(attendance.value)}</div>
          <div className="stat-change up">
            {attendance.change_percent >= 0 ? '↑' : '↓'} {Math.abs(attendance.change_percent || 0)}% vs last week
          </div>
          <div style={{ marginTop: '10px' }}>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${attendance.active_percent || 0}%`, background: 'var(--purple)' }}></div>
            </div>
            <div style={{ fontSize: '10px', color: 'var(--text-light)', marginTop: '3px' }}>
              {attendance.active_percent || 0}% of active members
            </div>
          </div>
          <div className="stat-icon"><Church size={20} /></div>
        </div>
      </div>

      {/* ================= QUICK ACTIONS ================= */}
      <div style={{ display: 'flex', gap: '10px', margin: '20px 0', flexWrap: 'wrap' }}>
        <button className="btn btn-gold btn-sm" onClick={() => handleNavigate('broadcast')}>
          <Megaphone size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Send Broadcast
        </button>
        <button className="btn btn-ghost btn-sm" onClick={() => handleNavigate('members')}>+ Add Member</button>
        <button className="btn btn-ghost btn-sm" onClick={() => handleNavigate('polls')}>
          <BarChart3 size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Create Poll
        </button>
        <button className="btn btn-ghost btn-sm" onClick={() => handleNavigate('events')}>
          <Calendar size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />New Event
        </button>
        <button className="btn btn-ghost btn-sm" onClick={() => handleNavigate('prayers')}>
          <HandHeart size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Prayer Wall
        </button>
        <button className="btn btn-ghost btn-sm" onClick={() => handleNavigate('giving')}>
          <DollarSign size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Giving Campaign
        </button>
      </div>

      {/* ================= MAIN GRID ================= */}
      <div className="grid-65-35" style={{ marginBottom: '20px' }}>
        {/* SMS Activity */}
        <div className="card">
          <div className="card-header">
            <div>
              <div className="card-title">SMS Activity — Last 12 Sundays</div>
              <div className="card-sub">Messages sent per service day</div>
            </div>
            <select className="form-select" style={{ width: 'auto', padding: '6px 10px', fontSize: '12px' }}>
              <option>Last 12 weeks</option>
              <option>Last 6 months</option>
            </select>
          </div>

          <div className="bar-chart" style={{ height: '130px', paddingTop: '10px' }}>
            {bars.length > 0 ? (
              bars.map((b, i) => (
                <div key={i} className="bar-col">
                  <div style={{ fontSize: '9px', color: 'var(--text-light)', marginBottom: '2px' }}>{b.value}</div>
                  <div
                    className="bar"
                    style={{
                      height: `${(b.value / barMax) * 100}px`,
                      background: i === bars.length - 1 ? 'var(--gold)' : 'var(--navy)',
                      opacity: i === bars.length - 1 ? 1 : 0.35 + i * 0.055,
                    }}
                  ></div>
                  <div className="bar-label">{b.short_label || b.label}</div>
                </div>
              ))
            ) : (
              <div style={{ width: '100%', textAlign: 'center', color: 'var(--text-light)', fontSize: '12px' }}>
                No SMS activity yet
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '16px', paddingTop: '12px', borderTop: '1px solid var(--border-light)', marginTop: '10px' }}>
            {[
              { l: 'Total Sent', v: formatNumber(totals.total_sent), c: 'var(--navy)' },
              { l: 'Delivered',  v: formatNumber(totals.delivered),  c: 'var(--green)' },
              { l: 'Responses',  v: formatNumber(totals.responses),  c: 'var(--blue)' },
              { l: 'Failed',     v: formatNumber(totals.failed),     c: 'var(--red)' },
            ].map((item) => (
              <div key={item.l} style={{ textAlign: 'center', flex: 1 }}>
                <div style={{ fontSize: '16px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: item.c }}>
                  {item.v}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>{item.l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Birthdays + Inbox */}
        <div>
          {/* Birthdays */}
          <div className="card" style={{ marginBottom: '16px' }}>
            <div className="card-header">
              <div className="card-title">
                Birthdays This Week <Cake size={16} style={{ verticalAlign: '-3px' }} />
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => handleNavigate('birthdays')}>All →</button>
            </div>

            {birthdays.length > 0 ? (
              birthdays.slice(0, 4).map((m) => (
                <div key={m.id} className="birthday-card">
                  <div className="bday-avatar" style={{ background: m.color || '#4f46e5' }}>
                    {m.initials || (m.name || '').split(' ').map(x => x[0]).join('').slice(0, 2)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className="bday-name">{m.name}</div>
                    <div className="bday-date">
                      <Calendar size={12} style={{ verticalAlign: '-2px', marginRight: '3px' }} />
                      {m.dob} · {m.group || '—'}
                    </div>
                  </div>
                  <div>
                    <span className={`badge ${m.days_until === 0 ? 'badge-green' : 'badge-amber'}`}>
                      {m.days_until === 0 ? (
                        <><PartyPopper size={12} style={{ verticalAlign: '-2px', marginRight: '3px' }} />Today</>
                      ) : `${m.days_until}d`}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div style={{ padding: '16px 0', textAlign: 'center', fontSize: '12px', color: 'var(--text-light)' }}>
                No birthdays this week
              </div>
            )}
          </div>

          {/* Inbox Alerts */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">
                Inbox Alerts <MessageSquare size={16} style={{ verticalAlign: '-3px' }} />
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => handleNavigate('inbox')}>Open →</button>
            </div>

            {inboxAlerts.length > 0 ? (
              inboxAlerts.slice(0, 3).map((m) => (
                <div
                  key={m.id}
                  className="inbox-item unread"
                  style={{ cursor: 'pointer', padding: '8px 0' }}
                  onClick={() => handleNavigate('inbox')}
                >
                  <div className="inbox-dot"></div>
                  <div
                    style={{
                      width: '28px', height: '28px', borderRadius: '50%',
                      background: m.color || '#4f46e5',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '11px', fontWeight: '700', color: 'white', flexShrink: 0,
                    }}
                  >
                    {m.initials || (m.name || '')[0]}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="inbox-name">{m.name}</div>
                    <div className="inbox-preview">{m.msg}</div>
                  </div>
                  <div className="inbox-time">{m.time}</div>
                </div>
              ))
            ) : (
              <div style={{ padding: '16px 0', textAlign: 'center', fontSize: '12px', color: 'var(--text-light)' }}>
                No unread messages
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ================= SECOND ROW ================= */}
      <div className="grid-3" style={{ marginBottom: '20px' }}>
        {/* Automations */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">
              Active Automations <Zap size={16} style={{ verticalAlign: '-3px' }} />
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => handleNavigate('automation')}>Manage</button>
          </div>

          {automations.length > 0 ? (
            automations.slice(0, 5).map((a) => (
              <div
                key={a.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '8px 0', borderBottom: '1px solid var(--border-light)',
                }}
              >
                <div
                  style={{
                    width: '30px', height: '30px', borderRadius: '8px',
                    background: `${a.color}20`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px',
                  }}
                >
                  {a.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--navy)' }}>{a.title}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>{a.sent} sent</div>
                </div>
                <span className="badge badge-green" style={{ fontSize: '10px' }}>ON</span>
              </div>
            ))
          ) : (
            <div style={{ padding: '16px 0', textAlign: 'center', fontSize: '12px', color: 'var(--text-light)' }}>
              No active automations
            </div>
          )}

          <button
            className="btn btn-ghost btn-sm"
            style={{ width: '100%', marginTop: '12px' }}
            onClick={() => handleNavigate('automation')}
          >
            + New Automation
          </button>
        </div>

        {/* Platform Health */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">Platform Health</div>
          </div>

          {healthItems.length > 0 ? (
            healthItems.map((s) => (
              <div key={s.label} style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ fontSize: '12px', color: 'var(--text-mid)' }}>{s.label}</span>
                  <span style={{ fontSize: '12px', fontWeight: '700', fontFamily: 'JetBrains Mono, monospace' }}>
                    {s.pct}%
                  </span>
                </div>
                <div className="progress-bar" style={{ height: '6px' }}>
                  <div className="progress-fill" style={{ width: `${s.pct}%`, background: s.color }}></div>
                </div>
              </div>
            ))
          ) : (
            <div style={{ padding: '16px 0', textAlign: 'center', fontSize: '12px', color: 'var(--text-light)' }}>
              No health data available
            </div>
          )}

          <div
            style={{
              marginTop: '8px', padding: '10px',
              background: 'var(--green-bg)', borderRadius: '8px',
              fontSize: '12px', color: 'var(--green)',
            }}
          >
            <CheckCircle2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />
            {platformHealth?.message || 'All systems operational'}
          </div>
        </div>

        {/* Latest Poll */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">
              Latest Poll <BarChart3 size={16} style={{ verticalAlign: '-3px' }} />
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => handleNavigate('polls')}>All polls</button>
          </div>

          {latestPoll ? (
            <>
              <div style={{ fontSize: '12px', color: 'var(--text-mid)', marginBottom: '12px' }}>
                {latestPoll.title} — {latestPoll.date}
              </div>

              {(latestPoll.results || []).map((r) => (
                <div key={r.label} className="poll-option">
                  <div className="poll-option-label" style={{ fontSize: '11px', minWidth: '90px' }}>{r.label}</div>
                  <div className="poll-bar-wrap">
                    <div className="poll-bar-fill" style={{ width: `${r.pct}%`, background: r.color }}></div>
                  </div>
                  <div className="poll-pct">{r.pct}%</div>
                </div>
              ))}

              <div style={{ fontSize: '11px', color: 'var(--text-light)', marginTop: '8px', textAlign: 'center' }}>
                {latestPoll.total_responses} / {latestPoll.target_responses} responded · Avg:{' '}
                {Array.from({ length: 4 }).map((_, i) => (
                  <Star key={i} size={11} fill="currentColor" style={{ verticalAlign: '-2px' }} />
                ))}{' '}
                {latestPoll.average_rating || '—'}/5
              </div>
            </>
          ) : (
            <div style={{ padding: '16px 0', textAlign: 'center', fontSize: '12px', color: 'var(--text-light)' }}>
              No polls yet
            </div>
          )}
        </div>
      </div>

      {/* ================= BOTTOM ROW ================= */}
      <div className="grid-2">
        {/* ---------------- Recent Activity (paginated) ---------------- */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">Recent Activity</div>
            <button className="btn btn-ghost btn-sm" onClick={() => handleNavigate('notifications')}>
              View all →
            </button>
          </div>

          {recentActivity.length > 0 ? (
            <>
              {paginatedActivity.map((a) => {
                const IconComponent = getActivityIcon(a.type);
                return (
                  <div
                    key={a.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '8px 0', borderBottom: '1px solid var(--border-light)',
                    }}
                  >
                    <div
                      style={{
                        width: '32px', height: '32px', borderRadius: '8px',
                        background: `${a.color}20`,
                        color: a.color,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}
                    >
                      <IconComponent size={16} strokeWidth={2} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '12px', color: 'var(--text)' }}>{a.text}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>{a.time}</div>
                    </div>
                  </div>
                );
              })}

              {/* ---------- Pagination controls ---------- */}
              {activityTotalPages > 1 && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingTop: '12px',
                    marginTop: '4px',
                    borderTop: '1px solid var(--border-light)',
                    gap: '8px',
                  }}
                >
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => goToActivityPage(activityPage - 1)}
                    disabled={activityPage === 1}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '4px',
                      opacity: activityPage === 1 ? 0.4 : 1,
                      cursor: activityPage === 1 ? 'not-allowed' : 'pointer',
                    }}
                    title="Previous page"
                  >
                    <ChevronLeft size={14} /> Prev
                  </button>

                  <div style={{ fontSize: '11px', color: 'var(--text-light)', fontFamily: 'JetBrains Mono, monospace' }}>
                    Page <strong style={{ color: 'var(--navy)' }}>{activityPage}</strong> of {activityTotalPages}
                    <span style={{ marginLeft: '6px' }}>
                      ({activityTotal} items)
                    </span>
                  </div>

                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => goToActivityPage(activityPage + 1)}
                    disabled={activityPage === activityTotalPages}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '4px',
                      opacity: activityPage === activityTotalPages ? 0.4 : 1,
                      cursor: activityPage === activityTotalPages ? 'not-allowed' : 'pointer',
                    }}
                    title="Next page"
                  >
                    Next <ChevronRight size={14} />
                  </button>
                </div>
              )}
            </>
          ) : (
            <div style={{ padding: '16px 0', textAlign: 'center', fontSize: '12px', color: 'var(--text-light)' }}>
              No recent activity
            </div>
          )}
        </div>

        {/* Groups Snapshot */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">Groups Snapshot</div>
            <button className="btn btn-ghost btn-sm" onClick={() => handleNavigate('groups')}>Manage →</button>
          </div>

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Group</th>
                  <th>Members</th>
                  <th>Engagement</th>
                  <th>SMS</th>
                </tr>
              </thead>
              <tbody>
                {groupsSnapshot.length > 0 ? (
                  groupsSnapshot.map((g) => (
                    <tr key={g.group}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <div
                            style={{
                              width: '8px', height: '8px', borderRadius: '50%',
                              background: g.color || '#4f46e5',
                            }}
                          ></div>
                          <span style={{ fontWeight: '500', fontSize: '13px' }}>{g.group}</span>
                        </div>
                      </td>
                      <td style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: '600' }}>
                        {g.members}
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <div className="progress-bar" style={{ width: '50px' }}>
                            <div
                              className="progress-fill"
                              style={{ width: `${g.engagement}%`, background: 'var(--green)' }}
                            ></div>
                          </div>
                          <span style={{ fontSize: '11px' }}>{g.engagement}%</span>
                        </div>
                      </td>
                      <td>
                        <button
                          className="btn btn-ghost btn-sm btn-icon"
                          onClick={() => showToast?.(`SMS sent to ${g.group}!`, 'success')}
                        >
                          <Send size={14} />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4">
                      <div className="empty-state" style={{ padding: '20px 0' }}>
                        <div className="empty-icon"><Users size={32} strokeWidth={1.5} /></div>
                        <div className="empty-title">No groups yet</div>
                        <div className="empty-text">Create groups to see them here</div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default Dashboard;